import { createClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';
type SanityImageSource = Parameters<ReturnType<typeof imageUrlBuilder>['image']>[0];

// TODO: Replace with your Sanity project ID and dataset
const projectId = import.meta.env.VITE_SANITY_PROJECT_ID || 'e4kgx7h1';
const dataset = import.meta.env.VITE_SANITY_DATASET || 'production';
const apiVersion = '2024-01-01';

export const client = createClient({
  projectId,
  dataset,
  apiVersion,
  useCdn: false, // Use API directly for fresh data after mutations
  perspective: 'published', // or 'previewDrafts' for draft content
});

// Image URL builder
const builder = imageUrlBuilder(client);

export function urlFor(source: SanityImageSource) {
  return builder.image(source);
}

/**
 * Read CMS content through the backend proxy function.
 *
 * The Sanity query API is origin-restricted (only the production domains are
 * allowlisted), so direct browser reads fail on preview URLs and the UI falls
 * back to hardcoded defaults. The proxy performs the read server-side and
 * returns permissive CORS headers, so reads work from every origin. If the
 * proxy is unreachable we fall back to a direct read.
 */
const proxyBase = (import.meta.env.VITE_SUPABASE_URL || '').replace(/\/$/, '');
const proxyUrl = proxyBase ? `${proxyBase}/functions/v1/sanity-query` : '';

export async function sanityFetch<T>(
  query: string,
  params?: Record<string, unknown>
): Promise<T> {
  if (proxyUrl) {
    try {
      const res = await fetch(proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, params: params ?? {} }),
      });
      if (res.ok) {
        const json = (await res.json()) as { result?: T };
        return json.result as T;
      }
      console.warn('sanity-query proxy responded', res.status);
    } catch (error) {
      console.warn('sanity-query proxy unavailable, falling back', error);
    }
  }
  return client.fetch<T>(query, params);
}

// Helper for getting typed documents
export async function getDocument<T>(type: string, slug?: string): Promise<T | null> {
  const query = slug
    ? `*[_type == "${type}" && slug.current == "${slug}"][0]`
    : `*[_type == "${type}"][0]`;

  return sanityFetch<T | null>(query);
}

// Helper for getting multiple documents
export async function getDocuments<T>(type: string, filter?: string): Promise<T[]> {
  const query = filter
    ? `*[_type == "${type}" && ${filter}]`
    : `*[_type == "${type}"]`;

  return sanityFetch<T[]>(query);
}


// Singleton document fetchers
export async function getSiteSettings() {
  return getDocument('siteSettings');
}

export async function getNavigation() {
  return getDocument('navigation');
}

export async function getFooter() {
  return getDocument('footer');
}

export async function getHomePage() {
  return getDocument('homePage');
}

export async function getStoryPage() {
  return getDocument('storyPage');
}

export async function getContactPage() {
  return getDocument('contactPage');
}

export async function getPropertyManagementPage() {
  return getDocument('propertyManagementPage');
}

export async function getJoinCollectionPage() {
  return getDocument('joinCollectionPage');
}

export async function getGuestLoungePage() {
  return getDocument('guestLoungePage');
}

// Property fetchers
export async function getProperty(slug: string) {
  return getDocument('property', slug);
}

export async function getAllProperties() {
  return getDocuments('property');
}

// Explore page fetchers
export async function getExplorePage(slug: string) {
  return getDocument('explorePage', slug);
}

// Experience fetchers
export async function getExperiencesByProperty(propertyId: string) {
  return client.fetch(
    `*[_type == "experience" && references($propertyId)]`,
    { propertyId }
  );
}

// Testimonial fetchers
export async function getFeaturedTestimonials() {
  return getDocuments('testimonial', 'featured == true');
}

// Social links
export async function getSocialLinks() {
  return getDocuments('socialLink');
}
